'use client';

import { useState, useEffect, useRef, useEffectEvent } from 'react';
import { useRouter } from 'next/navigation';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import { Loader2, FileText, ChevronLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage
} from '@/components/ui/form';
import { DatePicker } from '@/components/ui/date-picker';
import {
  ContactSearchField,
  type SelectedContact
} from '@/components/ui/contact-search-field';
import { LawFirmUser } from '@/lib/services/account/types';
import { getUserDisplayName } from '@/lib/utils/permissions';
import { createMatterWithTypeAction } from '@/lib/services/matters/client';
import { fetchSuggestedReference } from '@/lib/services/matters/client';
import { useAutoFillOwner } from '@/lib/hooks/forms';
import { DemoMatterCheckbox } from '@/components/matters/demo-matter-checkbox';
import { resolveDemoMatterFlag } from '@/lib/utils/demo-matter';
import { toast } from '@/lib/toast';
import { CreateMatterFormViewProps, CreateMatterPayload } from './types';
import { MatterRole, MatterRoleText } from '@/lib/services/matter-roles/types';
import { MultiSelectInput } from '../dashboard/multi-select-input';
import { PmsSyncPanel } from '@/components/matter-details/components/pms-sync-panel';
import type { SmokeballSyncConfig } from '@/components/matter-details/components/smokeball-sync-fields';
/**
 * Validation schema for matter creation form
 * Only Description and Owner are required for non-estate matters
 */
const createMatterSchema = z.object({
  description: z.string().min(1, 'Description is required'),
  matterReference: z.string().optional(),
  isDemo: z.boolean(),
  ownerId: z.string().min(1, 'Owner is required'),
  assistingIds: z.array(z.string()),
  scheduledDate: z.date().optional(),
  notes: z.string().optional()
});

type CreateMatterFormData = z.infer<typeof createMatterSchema>;

/**
 * CreateMatterFormView Component
 *
 * Displays a matter creation form for non-Estate Administration matter types.
 * Includes dynamic client name fields (1-4), owner/assisting dropdowns, and all required fields.
 */
export function CreateMatterFormView({
  matterType,
  onBack,
  onSuccess,
  hideBreadcrumb = false,
  existingMatter,
  isEditMode = false,
  visible = false
}: CreateMatterFormViewProps) {
  const router = useRouter();
  const queryClient = useQueryClient();
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Track if form has been initialized to prevent multiple resets
  const formInitializedRef = useRef(false);

  // Client contact selection state (using ContactSearchField)
  const [selectedClients, setSelectedClients] = useState<SelectedContact[]>(
    () =>
      isEditMode && existingMatter
        ? (existingMatter.clients?.map((c) => ({
            id: c.id,
            firstName: c.firstName,
            lastName: c.lastName,
            organisationName: c.organisationName,
            email: null
          })) ?? [])
        : []
  );

  // PMS sync config from PmsSyncPanel (create mode)
  const existingPms = (
    existingMatter?.metadata as
      | {
          pms?: {
            stateOfMatter?: string;
            matterTypeId?: string;
            representativeOption?: string;
          };
        }
      | undefined
  )?.pms;

  const [pmsConfig, setPmsConfig] = useState<SmokeballSyncConfig | null>(null);
  const [suggestedReference, setSuggestedReference] = useState<string | null>(
    null
  );

  // Fetch suggested reference when visible
  useEffect(() => {
    if (!visible) return;

    fetchSuggestedReference(existingMatter?.id)
      .then((value) => setSuggestedReference(value))
      .catch(() => {});
  }, [visible, existingMatter?.id]);

  // OPT: normalise "role" structure across task and matter
  // Matter returns {role.user.id}, Task returns {role.id}
  const assistingIds = (existingMatter?.roles || [])
    .filter(
      (r) =>
        r.role === MatterRoleText.ASSISTING_MEMBER ||
        r.role === MatterRole.ASSISTING_MEMBER
    )
    .map((r: { id?: string | number; user?: { id?: string | number } }) =>
      String(r.user?.id || r.id)
    );

  // Initialize form with React Hook Form
  const form = useForm<CreateMatterFormData>({
    resolver: zodResolver(createMatterSchema),
    defaultValues: {
      description: existingMatter?.metadata?.description || '',
      matterReference: existingMatter?.lawFirmInternalReference || '',
      isDemo: existingMatter?.isDemo ?? false,
      ownerId: existingMatter?.actingSolicitor?.id?.toString() || '',
      assistingIds,
      scheduledDate: existingMatter?.scheduledDate
        ? new Date(existingMatter.scheduledDate)
        : undefined,
      notes: existingMatter?.notes || ''
    }
  });

  // Reset form and flags when component mounts - runs only once per mount
  const initializeFormOnMount = useEffectEvent(() => {
    formInitializedRef.current = false;

    // In edit mode, mark as initialized and prevent auto-fill
    if (isEditMode && existingMatter) {
      formInitializedRef.current = true;

      // Reset form with existing matter data
      form.reset({
        description: existingMatter.metadata?.description || '',
        matterReference: existingMatter.lawFirmInternalReference || '',
        isDemo: existingMatter.isDemo ?? false,
        ownerId: existingMatter.actingSolicitor?.id?.toString() || '',
        assistingIds,
        scheduledDate: existingMatter.scheduledDate
          ? new Date(existingMatter.scheduledDate)
          : undefined,
        notes: existingMatter.notes || ''
      });

      // selectedClients is seeded from `existingMatter` via the useState
      // initializer above; nothing to do here.
    } else {
      // Reset form to clear any cached state when in create mode
      form.reset({
        description: '',
        matterReference: '',
        isDemo: false,
        ownerId: '',
        assistingIds: [],
        scheduledDate: undefined,
        notes: ''
      });
      formInitializedRef.current = true;
    }
  });
  useEffect(() => {
    initializeFormOnMount();
  }, []); // Empty deps - run only on mount

  // Determine matter type slug - prefer existingMatter.type in edit mode
  const effectiveMatterTypeSlug = isEditMode
    ? existingMatter?.type || matterType.slug
    : matterType.slug;

  // Auto-fill owner field with current user
  const { lawFirmUsers: users } = useAutoFillOwner({
    form,
    isEditMode,
    formInitialized: formInitializedRef.current
  });

  // Filter assisting users to exclude selected owner
  // eslint-disable-next-line react-hooks/incompatible-library -- informational -- react-hook-form's form.watch() is a known compiler-incompatible library call, so the component is skipped rather than miscompiled.
  const selectedOwnerId = form.watch('ownerId');
  const assistingUsers = users.filter(
    (u: LawFirmUser) => u.id.toString() !== selectedOwnerId
  );

  const assistingUsersOptions = assistingUsers.map((user: LawFirmUser) => ({
    value: String(user.id),
    label: getUserDisplayName(user)
  }));

  // Handle form submission
  const onSubmit = async (data: CreateMatterFormData) => {
    setIsSubmitting(true);

    try {
      const roles = [];
      if (data.ownerId) {
        roles.push({
          userId: Number(data.ownerId),
          role: MatterRole.ACTING_SOLICITOR
        });

        if (data.assistingIds?.length) {
          roles.push(
            ...data.assistingIds.map((id) => ({
              userId: Number(id),
              role: MatterRole.ASSISTING_MEMBER
            }))
          );
        }
      }

      // Get client IDs - all contacts have real IDs (created via API before selection)
      const clientIds = selectedClients.map((c) => c.id);

      // Build payload with clientIds instead of clientNames
      // Note: metadata.clientNames is intentionally NOT included - clients are
      // managed via clientIds at the top level of the payload
      // Resolved against the reference rather than read straight off the checkbox:
      // the control disappears once the reference stops saying DEMO, and a box
      // ticked beforehand must not keep submitting `true` from behind it.
      const isDemo = resolveDemoMatterFlag(data.matterReference, data.isDemo);

      const payload: CreateMatterPayload = {
        type: matterType.slug,
        metadata: {
          ...(data.description && { description: data.description }),
          ...(pmsConfig && { pms: pmsConfig })
        },
        isArchived: false,
        isDemo,
        ...(data.matterReference && {
          lawFirmInternalReference: data.matterReference
        }),
        ...(roles.length > 0 && { roles }),
        ...(data.notes && { notes: data.notes }),
        ...(clientIds.length > 0 && { clientIds })
      };

      // Format scheduled date if provided
      const scheduledDate = data.scheduledDate
        ? format(data.scheduledDate, 'yyyy-MM-dd')
        : undefined;

      if (isEditMode && existingMatter) {
        // Edit mode - update existing matter
        const { updateMatterAction } =
          await import('@/lib/services/matters/client');

        // Build update payload with all fields
        // Backend currently only supports notes, but we're preparing for full support
        // OPT: Should be using "UpdateMatterRequest" type but there are deep conflicts
        // Note: metadata.clientNames is intentionally NOT included - clients are
        // managed via clientIds. We only send description and pms in metadata
        // to avoid accidentally clearing or setting empty clientNames on the backend.
        const updatePayload: Record<string, unknown> = {
          notes: payload.notes,
          // These will work once backend adds support:
          type: payload.type,
          metadata: {
            description: data.description || undefined,
            ...(pmsConfig && { pms: pmsConfig })
          },
          lawFirmInternalReference: payload.lawFirmInternalReference,
          isDemo,
          ...(roles.length > 0 && { roles }),
          clientIds: selectedClients.map((c) => c.id),
          scheduledDate
        };

        const result = await updateMatterAction(
          existingMatter.id,
          // Structural conflicts between this in-flight shape and
          // UpdateMatterRequest are tracked above; cast at the boundary.
          updatePayload as unknown as Parameters<typeof updateMatterAction>[1]
        );

        if (!result.success) {
          throw new Error(result.error || 'Failed to update matter');
        }

        // Invalidate queries
        queryClient.invalidateQueries({ queryKey: ['matters'] });
        queryClient.invalidateQueries({ queryKey: ['tasks'] });

        toast.success('Matter updated successfully');
        onSuccess();
      } else {
        // Create mode - create new matter
        const result = await createMatterWithTypeAction(payload, scheduledDate);

        if (!result.success || !result.data) {
          throw new Error(result.error || 'Failed to create matter');
        }

        // Success! Invalidate queries to refetch without loading state
        queryClient.invalidateQueries({ queryKey: ['matters'] });
        queryClient.invalidateQueries({ queryKey: ['tasks'] });

        // No success toast - user is redirected to the dashboard with highlight
        onSuccess();

        const matterId = result.data.id;

        // Navigate to All Matters view with highlight
        router.push(`/all?highlight=${matterId}`);
      }
    } catch (error) {
      console.error('Failed to create matter:', error);
      toast.error(
        error instanceof Error ? error.message : 'Failed to create matter'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="p-6">
      {/* Breadcrumb Navigation - only show in Quick Actions modal */}
      {!hideBreadcrumb && (
        <div className="mb-6">
          <Button
            variant="ghost"
            size="auto"
            pad="flush"
            className="-ml-1"
            onClick={onBack}
            style={{
              color: 'var(--grey-45)',
              fontFamily: 'Inter',
              fontSize: '12px',
              fontWeight: 500,
              lineHeight: '16px',
              letterSpacing: '-0.2px'
            }}
          >
            <ChevronLeft className="mr-1 h-4 w-4" />
            Quick Actions
            <span
              style={{
                color: 'var(--grey-90)',
                fontFamily: 'Inter',
                fontSize: '12px',
                fontWeight: 500,
                lineHeight: '16px',
                letterSpacing: '-0.2px',
                padding: '0 6px'
              }}
            >
              /
            </span>
            {isEditMode ? 'Edit' : 'Create'} {matterType.label} Matter
          </Button>
        </div>
      )}

      {/* Form Title */}
      <div className="mb-6 flex items-center gap-2">
        <FileText className="text-muted-foreground h-5 w-5" />
        <h1
          style={{
            color: 'var(--grey-45)',
            fontFamily: 'var(--font-sans, Inter)',
            fontSize: '14px',
            fontWeight: 600,
            lineHeight: 'normal',
            letterSpacing: '-0.2px'
          }}
        >
          {matterType.label} Matter
        </h1>
      </div>

      {/* Form */}
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(onSubmit)}
          className="space-y-3 md:space-y-[1.125rem]"
        >
          {/* Description */}
          <FormField
            control={form.control}
            name="description"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  Description
                  <span className="text-destructive ms-1">*</span>
                </FormLabel>
                <FormControl>
                  <Input
                    placeholder="Add Description"
                    className="h-8 border-0 bg-[var(--input-bg)]"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Client Selection */}
          <ContactSearchField
            selectedContacts={selectedClients}
            onChange={setSelectedClients}
            label="Clients"
            placeholder="Search contacts to add as client..."
            allowCreate={true}
          />

          {/* Matter Reference */}
          <FormField
            control={form.control}
            name="matterReference"
            render={({ field }) => (
              <FormItem className="space-y-0.5">
                <div className="flex items-end justify-start">
                  <FormLabel>Matter Reference</FormLabel>
                  {suggestedReference && suggestedReference !== field.value && (
                    <span className="text-12px ms-1 flex items-center justify-end gap-x-0.5 leading-4 font-medium tracking-tight text-[var(--label-foreground)]">
                      - Suggested Ref
                      <Button
                        density="micro"
                        pad="2xs"
                        weight="bold"
                        type="button"
                        variant="ghost"
                        size="5xs"
                        onClick={() =>
                          form.setValue('matterReference', suggestedReference)
                        }
                      >
                        {suggestedReference}
                      </Button>
                    </span>
                  )}
                </div>
                <FormControl>
                  <Input
                    placeholder="Add Matter Reference"
                    className="h-8 border-0 bg-[var(--input-bg)]"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {/*
            Demo matter — only offered once the reference says DEMO. Rendered
            without a FormField/FormItem wrapper deliberately: FormItem is a
            `div`, so it would still take a margin from the form's `space-y-*`
            while contributing no height, leaving a double gap here on every
            matter whose reference does not qualify — which is nearly all of them.
          */}
          <DemoMatterCheckbox
            id="quick-actions-is-demo-matter"
            reference={form.watch('matterReference')}
            checked={form.watch('isDemo')}
            onCheckedChange={(value) => form.setValue('isDemo', value)}
          />

          {/* Owner, Assisting, and Scheduled Date - Side by side on larger screens */}
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            {/* Owner */}
            <FormField
              control={form.control}
              name="ownerId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    Owner
                    <span className="text-destructive ms-1">*</span>
                  </FormLabel>
                  <Select
                    onValueChange={field.onChange}
                    value={field.value}
                    defaultValue={field.value}
                  >
                    <FormControl>
                      <SelectTrigger className="h-8 border-0 bg-[var(--input-bg)]">
                        <SelectValue placeholder="Select Owner" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {users.map((user: LawFirmUser) => (
                        <SelectItem key={user.id} value={user.id.toString()}>
                          {getUserDisplayName(user)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Assisting */}
            <FormField
              control={form.control}
              name="assistingIds"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Assisting</FormLabel>
                  <FormControl className="w-full">
                    <MultiSelectInput
                      className="w-full"
                      placeholder="Select Assisting"
                      options={assistingUsersOptions}
                      selectedValues={field.value}
                      onSelectionChange={field.onChange}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Scheduled Date */}
            <FormField
              control={form.control}
              name="scheduledDate"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>When are you working on this matter?</FormLabel>
                  <FormControl>
                    <DatePicker
                      date={field.value}
                      onDateChange={field.onChange}
                      placeholder="Add Date"
                      className="h-8 border-0 bg-[var(--input-bg)] hover:bg-[var(--input-bg)]"
                      minDate={new Date(new Date().setHours(0, 0, 0, 0))}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          {/* Notes */}
          <FormField
            control={form.control}
            name="notes"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Notes </FormLabel>
                <FormControl>
                  <Textarea
                    placeholder="Add Notes"
                    rows={4}
                    className="border-0 bg-[var(--input-bg)]"
                    {...field}
                    value={field.value || ''}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* PMS Sync Configuration */}
          <PmsSyncPanel
            mode="create"
            matterTypeSlug={effectiveMatterTypeSlug}
            existingConfig={existingPms}
            onConfigChange={setPmsConfig}
            hasReferenceNumber={!!form.watch('matterReference')?.trim()}
          />

          {/* Submit Button */}
          <div className="flex justify-end">
            <Button
              size="xs"
              density="compact"
              gap="standard"
              type="submit"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  {isEditMode ? 'Saving...' : 'Creating matter...'}
                </>
              ) : isEditMode ? (
                'Save Changes'
              ) : (
                'Create Matter'
              )}
            </Button>
          </div>
        </form>
      </Form>
    </div>
  );
}
